import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const MEETING_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'special', label: 'Special' },
  { value: 'agm', label: 'AGM' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'committee', label: 'Committee' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS = {
  regular: 'bg-blue-100 text-blue-700',
  special: 'bg-purple-100 text-purple-700',
  agm: 'bg-green-100 text-green-700',
  emergency: 'bg-red-100 text-red-700',
  committee: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-700',
};

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

export default function AdminMinute() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState({
    title: '', meeting_type: 'regular', meeting_date: '', venue: '', description: '', is_published: false,
  });
  const [file, setFile] = useState(null);

  const fetchMinutes = useCallback(() => {
    setLoading(true);
    let url = '/admin/minutes/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    if (typeFilter) url += `meeting_type=${typeFilter}&`;
    api.get(url)
      .then(r => setMinutes(r.data.results || r.data))
      .catch(() => toast.error('Failed to load minutes'))
      .finally(() => setLoading(false));
  }, [search, typeFilter]);

  useEffect(() => { fetchMinutes(); }, [fetchMinutes]);

  const resetForm = () => {
    setForm({ title: '', meeting_type: 'regular', meeting_date: '', venue: '', description: '', is_published: false });
    setFile(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (m) => {
    setForm({
      title: m.title, meeting_type: m.meeting_type, meeting_date: m.meeting_date || '',
      venue: m.venue || '', description: m.description || '', is_published: m.is_published,
    });
    setFile(null);
    setEditing(m);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('meeting_type', form.meeting_type);
    fd.append('meeting_date', form.meeting_date);
    if (form.venue) fd.append('venue', form.venue);
    if (form.description) fd.append('description', form.description);
    fd.append('is_published', form.is_published);
    if (file) fd.append('file', file);

    try {
      if (editing) {
        await api.patch(`/admin/minutes/${editing.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Minute updated');
      } else {
        await api.post('/admin/minutes/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Minute uploaded');
      }
      resetForm();
      fetchMinutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save minute');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this minute?')) return;
    try {
      await api.delete(`/admin/minutes/${id}/`);
      toast.success('Minute deleted');
      fetchMinutes();
    } catch { toast.error('Failed to delete minute'); }
  };

  const togglePublish = async (m) => {
    try {
      await api.patch(`/admin/minutes/${m.id}/`, { is_published: !m.is_published });
      toast.success(m.is_published ? 'Unpublished' : 'Published');
      fetchMinutes();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Meeting Minutes</h1>
      <p className="text-sm text-gray-500 mb-6">Upload and manage meeting minutes for members to view and download</p>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search minutes..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]">
          <option value="">All Types</option>
          {MEETING_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canAdd(PERMISSIONS.ADD_MINUTES) && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition whitespace-nowrap">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Upload Minute
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Minute' : 'Upload Minute'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormInput label="Title *" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Meeting Type</label>
              <select value={form.meeting_type} onChange={e => setForm({ ...form, meeting_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                {MEETING_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <FormInput label="Meeting Date *" type="date" required value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} />
            <FormInput label="Venue" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">File (PDF/DOC) {!editing && '*'}</label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border rounded-lg text-sm" required={!editing} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                <span className="text-sm font-medium text-gray-600">Publish immediately</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
              {editing ? 'Update' : 'Upload'}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : minutes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Minutes</h2>
          <p className="text-gray-400">Upload your first meeting minute.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">S/N</th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Venue</th>
                <th className="px-4 py-3 text-center">Published</th>
                <th className="px-4 py-3 text-left">Uploaded By</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {minutes.map((m, idx) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{m.title}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TYPE_COLORS[m.meeting_type] || 'bg-gray-100 text-gray-600'}`}>
                      {m.meeting_type_display || m.meeting_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{m.meeting_date}</td>
                  <td className="px-4 py-3 text-gray-600">{m.venue || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {canEdit(PERMISSIONS.EDIT_MINUTES) ? (
                      <button onClick={() => togglePublish(m)}
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold cursor-pointer ${m.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {m.is_published ? 'Yes' : 'No'}
                      </button>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${m.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {m.is_published ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{m.uploaded_by_name || '—'}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {m.file && (
                      <a href={m.file} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline text-xs">Download</a>
                    )}
                    {canEdit(PERMISSIONS.EDIT_MINUTES) && (
                      <button onClick={() => handleEdit(m)} className="text-blue-600 hover:underline text-xs">Edit</button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_MINUTES) && (
                      <button onClick={() => handleDelete(m.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
